import React, { Component } from 'react'
import { AiOutlineCheckCircle, AiOutlineWarning, AiOutlineCloseCircle } from "react-icons/ai"
import { getInformationWithData, getInformationWithDataV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import "../../css/general-style.css"
import '../../css/dashboard.css'
import { Menu } from "../../components/Menu/Menu"

const BTN_PRIMARY = { background: '#2563EB', border: 'none', borderRadius: '8px', color: '#FFFFFF', fontWeight: 700, padding: '0.5rem 1.1rem', fontSize: '0.85rem', cursor: 'pointer' }
const BTN_SECONDARY = { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#0F172A', fontWeight: 600, padding: '0.4rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer' }
const BTN_DISABLED = { ...BTN_PRIMARY, background: '#94A3B8', cursor: 'not-allowed' }
const INPUT_STYLE = { border: '1px solid #E2E8F0', borderRadius: '8px', padding: '0.5rem 0.75rem', fontSize: '0.9rem', width: '220px' }

const errorMessageOf = (response) => {
    if (response && response.message) return response.message
    if (response && response.error) return response.error
    return ''
}

/**
 * Tools → Customer Correction (2026-09-25).
 *
 * Real case that motivated this: an SO gets created against a placeholder
 * "Cash Sale" account, then someone fixes the real customer directly in
 * Sage (manual MAS90 edit — the portal has no UI to reassign a SO's
 * customer, and the read-only mas90-query-bridge can't write it either).
 * From that point on, Invoices V3's own fraud/mismatch check (built for the
 * old "Altitude bills the wrong customer" WS bug) keeps comparing the live,
 * correct customer against the stale creation-time snapshot and blocks the
 * invoice — a false positive on a deliberate, legitimate change.
 *
 * This tool does NOT touch Sage — the actual reassignment stays a manual
 * MAS90 step (see soNumberGuard.ts's sibling discussion: writing to Sage
 * either needs the untested risk of a full SO resend, or a proper MAS90-side
 * job, neither built yet). It only pulls the customer Sage ALREADY has
 * (live) and records that as the new reference point for the mismatch
 * check — an APPEND to document_audit_trail, never an overwrite, so the
 * true creation-time value is never lost (see documentAuditTrail.ts's
 * correctCustomer() header comment for the full design rationale).
 */
export class CustomerCorrectionTool extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isAdmin: getValueCookie('TypeUser') === '1',
            docNo: '',
            idCompany: getValueCookie('CompanyId') === '6' ? 6 : 3,
            checking: false,
            checkError: '',
            preview: null,       // { liveCustomerNo, liveCustomerName, lastCustomerNo, lastCustomerName, alreadyMatches }
            applying: false,
            applyResult: null,   // { changed, customerNo, customerName, previousCustomerNo, previousCustomerName } or { error }
        }
    }

    handleCheck = async () => {
        const docNo = this.state.docNo.trim()
        if (!docNo) return
        this.setState({ checking: true, checkError: '', preview: null, applyResult: null })

        const idCompany = this.state.idCompany
        // The 4 route prefixes (/my, /alt, /ne, base) all point to the exact
        // same handler, which picks the real Sage client from the idCompany
        // field in the body, not from which prefix was used — so whichever
        // apiPrefix cookie this session already has works fine here
        // regardless of the Company dropdown above.
        const liveResponse = await getInformationWithData('/sales/get/header/by-no', { SalesOrderNo: docNo, idCompany })
        const liveRow = liveResponse && liveResponse.data && liveResponse.data[0] ? liveResponse.data[0] : null
        if (!liveRow || !liveRow.CustomerNo) {
            this.setState({ checking: false, checkError: `No se encontró ${docNo} en vivo en Sage para esta empresa.` })
            return
        }

        const lastResponse = await getInformationWithDataV3('/v3/audit/original-customer', { docNo, idCompany })
        const lastData = lastResponse && lastResponse.status && lastResponse.status.code === 1 ? lastResponse.data : null
        const lastFound = !!(lastData && lastData.found)

        const liveCustomerNo = String(liveRow.CustomerNo)
        const lastCustomerNo = lastFound ? String(lastData.customerNo) : null

        this.setState({
            checking: false,
            preview: {
                liveCustomerNo,
                liveCustomerName: liveRow.BillToName || '',
                lastFound,
                lastCustomerNo,
                lastCustomerName: lastFound ? lastData.customerName : '',
                alreadyMatches: lastFound && lastCustomerNo === liveCustomerNo,
            },
        })
    }

    handleApply = async () => {
        const docNo = this.state.docNo.trim()
        this.setState({ applying: true })
        const response = await getInformationWithDataV3('/v3/audit/correct-customer', { docNo, idCompany: this.state.idCompany, reason: 'Corregido desde Tools: Customer Correction' })
        if (response && response.status && response.status.code === 1) {
            this.setState({ applying: false, applyResult: response.data })
        } else {
            this.setState({ applying: false, applyResult: { error: errorMessageOf(response) || 'No se pudo aplicar la corrección.' } })
        }
    }

    render() {
        const { isAdmin, docNo, idCompany, checking, checkError, preview, applying, applyResult } = this.state

        if (!isAdmin) {
            return (
                <div className="dash-page" style={{ paddingTop: '70px' }}>
                    <Menu />
                    <div style={{ padding: '2rem' }}>This tool is only available to administrators.</div>
                </div>
            )
        }

        return (
            <div className="dash-page" style={{ paddingTop: '70px' }}>
                <Menu />

                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '2rem 2rem' }}>
                    <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.5rem', margin: 0 }}>Customer Correction</h1>
                    <p style={{ color: 'rgba(255,255,255,0.65)', margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                        For a Sales Order whose customer was reassigned directly in Sage (e.g. a Cash Sale placeholder corrected to the real customer) — unblocks Invoices V3's customer-mismatch check without erasing the original audit record.
                    </p>
                </div>

                <div style={{ padding: '1.5rem 2rem', maxWidth: '720px' }}>
                    <div className="card mb-3">
                        <div className="card-body">
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '0.25rem' }}>Sales Order No.</label>
                                    <input style={INPUT_STYLE} value={docNo} placeholder="e.g. PM69908"
                                        onChange={(e) => this.setState({ docNo: e.target.value, preview: null, applyResult: null })} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#64748B', marginBottom: '0.25rem' }}>Company</label>
                                    <select style={INPUT_STYLE} value={idCompany}
                                        onChange={(e) => this.setState({ idCompany: Number(e.target.value), preview: null, applyResult: null })}>
                                        <option value={3}>Mayaland (PM)</option>
                                        <option value={6}>Altitude (AL)</option>
                                    </select>
                                </div>
                                <button style={checking || !docNo.trim() ? BTN_DISABLED : BTN_PRIMARY} disabled={checking || !docNo.trim()} onClick={this.handleCheck}>
                                    {checking ? 'Checking...' : 'Check in Sage'}
                                </button>
                            </div>

                            {checking && (
                                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748B', fontSize: '0.85rem' }}>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Looking up the live Sage record...
                                </div>
                            )}

                            {checkError && (
                                <div style={{ marginTop: '0.75rem', color: '#DC2626', fontSize: '0.85rem' }}>{checkError}</div>
                            )}

                            {preview && (
                                <div style={{ marginTop: '1rem', padding: '0.9rem 1rem', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                                        <strong>Live in Sage right now:</strong> {preview.liveCustomerName} ({preview.liveCustomerNo})
                                    </div>
                                    <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                                        <strong>Last recorded in the portal:</strong> {preview.lastFound ? `${preview.lastCustomerName} (${preview.lastCustomerNo})` : 'no record yet (SO created before this tool existed)'}
                                    </div>

                                    {preview.alreadyMatches ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#16A34A', fontSize: '0.85rem' }}>
                                            <AiOutlineCheckCircle /> Already matches — nothing to correct.
                                        </div>
                                    ) : !applyResult ? (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#B45309', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
                                                <AiOutlineWarning /> These differ — this is what's blocking Invoices V3's customer-mismatch check.
                                            </div>
                                            <button style={applying ? BTN_DISABLED : BTN_PRIMARY} disabled={applying} onClick={this.handleApply}>
                                                {applying ? 'Applying...' : 'Apply correction'}
                                            </button>
                                        </div>
                                    ) : null}

                                    {applyResult && applyResult.error && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#DC2626', fontSize: '0.85rem' }}>
                                            <AiOutlineCloseCircle /> {applyResult.error}
                                        </div>
                                    )}
                                    {applyResult && !applyResult.error && applyResult.changed && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#16A34A', fontSize: '0.85rem' }}>
                                            <AiOutlineCheckCircle /> Corrected — {docNo} now points to {applyResult.customerName} ({applyResult.customerNo}). The original creation record was kept, not erased.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <p style={{ fontSize: '0.75rem', color: '#94A3B8', fontStyle: 'italic' }}>
                        This only updates the portal's own reference records. It does not change anything in Sage — the customer reassignment on the Sales Order itself must already be done directly in MAS90 before using this tool.
                    </p>
                </div>
            </div>
        )
    }
}

export default CustomerCorrectionTool
